import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { usePortalStore } from '../../../stores/portalStore';
import { portalApi, portalApiError } from '../../../lib/portalApi';

interface LoginPayload { student_number: string; password: string }
interface LoginResponse {
  access_token: string;
  portal: { tenant_id: string; tenant_name: string; tenant_slug: string };
  student_id: string;
}

export function StudentLoginPage() {
  const navigate = useNavigate();
  const setAuth = usePortalStore(s => s.setAuth);

  const [form, setForm] = useState<LoginPayload>({ student_number: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (payload: LoginPayload) =>
      portalApi.post<{ data: LoginResponse }>('/portal/student/auth/login', payload).then(r => r.data.data),
    onSuccess: (data) => {
      setAuth({
        role: 'student',
        accessToken: data.access_token,
        userId: data.student_id,
        displayName: form.student_number,
        tenantSlug: data.portal.tenant_slug,
        tenantName: data.portal.tenant_name,
      });
      navigate('/portal/student');
    },
    onError: (err) => setError(portalApiError(err)),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    mutation.mutate(form);
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)] flex items-center justify-center mx-auto mb-3">
            <BookOpen size={22} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Student Portal</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Sign in to view your grades, fees, and reports</p>
        </div>

        {/* Card */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-secondary)]">Student Number</label>
              <input
                className="input"
                placeholder="e.g. STU-2024-0001"
                value={form.student_number}
                onChange={e => setForm(f => ({ ...f, student_number: e.target.value }))}
                autoFocus
                required
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-secondary)]">Password</label>
              <div className="relative">
                <input
                  className="input pr-10"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-xs text-[var(--color-danger)] bg-[var(--color-danger)]/5 border border-[var(--color-danger)]/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary w-full mt-1"
            >
              {mutation.isPending ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-xs text-[var(--color-text-muted)] text-center mt-4">
            Parent?{' '}
            <a href="/portal/parent/login" className="text-[var(--color-primary)] hover:underline">
              Parent Portal →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
